import type React from 'react';
import { useCallback, useRef, useState } from 'react';

import { openToastAlert } from '../../../../../../common/ToastAlert/toastAlert';
import type { DragHandlers, TagSheetParseResult } from '../../types';
import type { DropError } from '../../validation/validateDrop';
import { getUpdatedParseResultAfterDrop } from './utils';

type SetParseResult = React.Dispatch<React.SetStateAction<TagSheetParseResult | null>>;

export type ValidateLineItemToDropFn = (adId: string, lineItemId: string) => DropError | null;

const useLineItemsDragDrop = (
  setParseResult: SetParseResult,
  validateLineItemToDrop: ValidateLineItemToDropFn
): DragHandlers => {
  const [draggedAdId, setDraggedAdId] = useState<string | null>(null);
  const [dropTargetGroupId, setDropTargetGroupId] = useState<string | null>(null);
  const [dropTargetGroupForbidden, setDropTargetGroupForbidden] = useState(false);
  const draggedAdIdRef = useRef<string | null>(null);

  const onDragStart = useCallback(
    (adId: string) => (e: React.DragEvent) => {
      draggedAdIdRef.current = adId;
      setDraggedAdId(adId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', adId);
    },
    []
  );

  const onDragEnd = useCallback(() => {
    draggedAdIdRef.current = null;
    setDraggedAdId(null);
    setDropTargetGroupId(null);
    setDropTargetGroupForbidden(false);
  }, []);

  const onDragOver = useCallback(
    (groupId: string) => (e: React.DragEvent) => {
      if (!draggedAdIdRef.current) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDropTargetGroupId(groupId);

      const error = validateLineItemToDrop(draggedAdIdRef.current, groupId);
      setDropTargetGroupForbidden(Boolean(error));
    },
    [validateLineItemToDrop]
  );

  const onDragLeave = useCallback(() => {
    setDropTargetGroupId(null);
    setDropTargetGroupForbidden(false);
  }, []);

  const onDrop = useCallback(
    (targetGroupId: string) => (e: React.DragEvent) => {
      e.preventDefault();
      const adId = draggedAdIdRef.current;

      if (!adId) return;

      const error = validateLineItemToDrop(adId, targetGroupId);

      if (error) {
        openToastAlert({
          alertType: 'error',
          message: error.title,
          description: error.message,
          restrictToPage: false,
        });
      } else {
        setParseResult((prev) => {
          if (!prev) return prev;

          return getUpdatedParseResultAfterDrop(prev, adId, targetGroupId);
        });
      }

      draggedAdIdRef.current = null;
      setDraggedAdId(null);
      setDropTargetGroupId(null);
      setDropTargetGroupForbidden(false);
    },
    [setParseResult, validateLineItemToDrop]
  );

  return {
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
    draggedAdId,
    dropTargetGroupId,
    dropTargetGroupForbidden,
  };
};

export default useLineItemsDragDrop;
